import { Tone } from 'components/DocRightRail/screens/AIAssistant/ToneSelect'
import { MenuTrigger } from 'components/MenuTrigger/MenuTrigger'
import { Button } from 'blaze-ui/Button'
import { Menu } from 'components/Menu/Menu'
import { Item, Section } from '@react-stately/collections'
import { BrandVoice } from 'almanac-ui/icons'
import { MenuItemKey, MenuItemType, ToneMenuItem, VoiceAndTonesSelectProps, VoiceMenuItem } from './Types'
import { SelectableItem } from 'components/Menu/MenuItem'
import { useState } from 'react'
import { DirectionalChevron } from 'components/DirectionalChevron/DirectionalChevron'
import { Plus } from 'blaze-ui/icons'
import Styles from './VoiceAndTonesSelect.module.scss'
import { useCurrentWorkspace } from 'hooks/useCurrentWorkspace'
import { useOpenBrandVoiceModal } from 'screens/BrandKits/components/BrandVoice/useOpenBrandVoiceModal'
import { AnalysingContentModal } from 'screens/BrandKits/components/BrandVoice/AddVoice/AnalysingContentModal'
import { EditVoiceModal } from 'screens/BrandKits/components/BrandVoice/EditVoiceModal'
import { useModals } from 'almanac-ui/Modal'
import { observer } from 'mobx-react-lite'
import { useRootStore } from 'stores/RootStore'

export const VoiceAndTonesSelect = observer(
  ({
    onToneSelect,
    onVoiceSelect,
    selectedTones,
    selectedVoiceIds,
    disabledTones,
    disabledVoiceIds,
    voices,
    tones,
    size = 'lg',
    label = 'Pick a brand voice or up to 3 tones…',
    ButtonComponent = Button,
    popoverClassName,
  }: VoiceAndTonesSelectProps) => {
    const { openModal, closeModals, closeStack } = useModals()
    const { drawersStore } = useRootStore()
    const openBrandVoiceModal = useOpenBrandVoiceModal()
    const currentWorkspace = useCurrentWorkspace()
    const voiceItems: VoiceMenuItem[] = voices.map((voice) => ({
      id: voice.id,
      value: voice,
      type: MenuItemType.Voice,
      key: `${MenuItemType.Voice}:${voice.id}`,
    }))

    const toneItems: ToneMenuItem[] = tones.map((tone) => ({
      id: tone,
      value: tone,
      type: MenuItemType.Tone,
      key: `${MenuItemType.Tone}:${tone}`,
    }))

    const selectedVoiceItems = voiceItems.filter((voice) => selectedVoiceIds.includes(voice.id))
    const selectedToneItems = toneItems.filter((tone) => selectedTones.includes(tone.value))

    const openAddVoiceModal = () => {
      if (!currentWorkspace) return
      drawersStore.closeAllDrawers()
      openBrandVoiceModal(
        {
          onAnalyse: (analyseProps) =>
            openModal(AnalysingContentModal, {
              ...analyseProps,
              onNext: (editProps) =>
                openModal(EditVoiceModal, {
                  ...editProps,
                  onBack: () => {
                    closeModals(2)
                  },
                  onCreate: ({ voiceId }) => {
                    closeStack()
                    onVoiceSelect(voiceId)
                  },
                }),
            }),
        },
        { newStack: true },
      )
    }

    const handleAction = async (item: MenuItemKey | 'create_new_voice') => {
      if (item === 'create_new_voice') {
        return openAddVoiceModal()
      }

      const [type, value] = item.split(':')
      switch (type) {
        case MenuItemType.Voice:
          return onVoiceSelect(parseInt(value))
        case MenuItemType.Tone:
          return onToneSelect(value as Tone)
      }
    }

    const selectedKeys = [...selectedVoiceItems, ...selectedToneItems].map((item) => item.key)
    const disabledToneKeys = toneItems.filter((tone) => disabledTones?.includes(tone.value)).map((tone) => tone.key)
    const disabledVoiceKeys = voiceItems
      .filter((voice) => disabledVoiceIds?.includes(voice.id))
      .map((voice) => voice.key)
    const disabledKeys = [...disabledToneKeys, ...disabledVoiceKeys]
    const selectedItemLabels = [
      ...selectedVoiceItems.map((voiceItem) => voiceItem.value.voice_params.name),
      ...selectedTones,
    ]
    const [isOpen, setIsOpen] = useState(false)

    return (
      <MenuTrigger onOpenChange={setIsOpen} popoverClassName={popoverClassName}>
        <ButtonComponent
          endIcon={() => <DirectionalChevron direction={isOpen ? 'up' : 'down'} />}
          variant="secondary"
          aria-label="Select brand voice or tone"
          size={size}
          justifyContent="space-between"
        >
          <span className={Styles.buttonText}>
            {selectedItemLabels.length === 0 ? label : selectedItemLabels.join(', ')}
          </span>
        </ButtonComponent>
        <Menu
          selectionMode="multiple"
          onAction={(key) => handleAction(key as MenuItemKey)}
          selectedKeys={selectedKeys}
          disabledKeys={disabledKeys}
        >
          <Section title="Brand voices">
            {[
              ...voiceItems.map(({ key, value }) => (
                <Item key={key} textValue={value.voice_params.name}>
                  <BrandVoice />
                  {value.voice_params.name}
                </Item>
              )),
              <Item key="create_new_voice" textValue="Create New Brand Voice">
                <span className={Styles.createVoice}>
                  <Plus color="var(--selected-fill)" /> Create New Brand Voice
                </span>
              </Item>,
            ]}
          </Section>
          <Section title="Tones">
            {toneItems.map(({ key, value }) => (
              <SelectableItem key={key} closeOnSelect={false} type="checkbox" textValue={value}>
                {value}
              </SelectableItem>
            ))}
          </Section>
        </Menu>
      </MenuTrigger>
    )
  },
)
